import { anyLeague } from "../../../classificators/league/index.js";
import { IterableDataProcessor, RankingStat } from "../../../entities/index.js";
import { PitchingRankingStats, RankingScopeContext } from "../../../enums/index.js";
import EnumValueProvider from "../../../providers/EnumValueProvider.js";
import RankingStatCalculatorBase from "./../RankingStatCalculatorBase.js";
import NumberFormatter from "../../../formatters/NumberFormatter.js";
import ResultsSlice from "../../../valueObjects/ResultsSlice.js";
import satisfiesAll from "../../../classificators/satisfiesAll.js";
import pitchingScopeContext from "../../../classificators/resultsSlice/pitchingScopeContext.js";
import RankingStatCaseVerifier from "../../../validators/RankingStatCaseVerifier.js";
import sessionWithStatGrabScoring from "../../../classificators/session/sessionWithStatGrabScoring.js";
import resultsSliceForPitcher from "../../../classificators/resultsSlice/resultsSliceForPitcher.js";
import NewStrikeoutsCalculator from "./NewStrikeoutsCalculator.js";
import NewBaseHitsCalculator from "./NewBaseHitsCalculator.js";
import NewBaseOnBallsCalculator from "./NewBaseOnBallsCalculator.js";
import NewInningsPitchedCalculator from "./NewInningsPitchedCalculator.js";
import NewHitByPitchesCalculator from "./NewHitByPitchesCalculator.js"; 

/**
 * FIP is similar to ERA, but it focuses solely on the events a pitcher has the most control over -- strikeouts, unintentional walks, hit-by-pitches and home runs.
 * It entirely removes results on balls hit into the field of play.
 * @see https://www.mlb.com/glossary/advanced-stats/fielding-independent-pitching
 * @see https://www.baseball-reference.com/bullpen/Fielding_Independent_Pitching
 */
class NewFieldingIndependentPitchingCalculator extends RankingStatCalculatorBase {
    constructor(
        fipConstant,
        customResultsProcessor, rankingStatCaseVerifier, numberFormatter,
        strikeoutCalculator, hitsCalculator, walksCalculator, hitByPitchesCalculator, inningsPitchedCalculator
    ) {
        if (typeof(customResultsProcessor) !== 'undefined' && !(customResultsProcessor instanceof IterableDataProcessor)) {
            throw new TypeError('customResultsProcessor must be an instance inheriting from IterableDataProcessor class');
        }
        if (typeof(numberFormatter) !== 'undefined' && !(numberFormatter instanceof NumberFormatter)) {
            throw new TypeError('numberFormatter must be an instance inheriting from NumberFormatter class');
        }
        if (typeof(strikeoutCalculator) !== 'undefined' && !(strikeoutCalculator instanceof RankingStatCalculatorBase)) {
            throw new TypeError('strikeoutCalculator must be an instance inheriting from RankingStatCalculatorBase class');
        }
        if (typeof(hitsCalculator) !== 'undefined' && !(hitsCalculator instanceof RankingStatCalculatorBase)) {
            throw new TypeError('hitsCalculator must be an instance inheriting from RankingStatCalculatorBase class');
        }
        if (typeof(walksCalculator) !== 'undefined' && !(walksCalculator instanceof RankingStatCalculatorBase)) {
            throw new TypeError('walksCalculator must be an instance inheriting from RankingStatCalculatorBase class');
        }
        if (typeof(inningsPitchedCalculator) !== 'undefined' && !(inningsPitchedCalculator instanceof RankingStatCalculatorBase)) {
            throw new TypeError('inningsPitchedCalculator must be an instance inheriting from RankingStatCalculatorBase class');
        }
        if (typeof(hitByPitchesCalculator) !== 'undefined' && !(hitByPitchesCalculator instanceof RankingStatCalculatorBase)) {
            throw new TypeError('hitByPitchesCalculator must be an instance inheriting from RankingStatCalculatorBase class');
        }

        if (typeof(fipConstant) === 'undefined') {
            fipConstant = NewFieldingIndependentPitchingCalculator.fipConstantForAmateurs();
        }
        else if (typeof(fipConstant) !== 'number' || Number.isNaN(fipConstant)) {
            throw new TypeError('fipConstant must be an number');
        }

        const caseVerifier = rankingStatCaseVerifier || new RankingStatCaseVerifier(
            anyLeague,
            sessionWithStatGrabScoring,
            satisfiesAll(
                pitchingScopeContext,
                resultsSliceForPitcher
            )
        );

        super(
            [ PitchingRankingStats.FieldingIndependentPitching ],
            caseVerifier,
        );

        this.numberFormatter = numberFormatter || new NumberFormatter(3);
        this.resultsProcessor = customResultsProcessor || new IterableDataProcessor(
            new EnumValueProvider()
        );

        this.strikeoutCalculator = strikeoutCalculator || new NewStrikeoutsCalculator(
            RankingScopeContext.Pitching.key, this.resultsProcessor, caseVerifier
        );
        this.hitsCalculator = hitsCalculator || new NewBaseHitsCalculator(
            RankingScopeContext.Pitching.key, this.resultsProcessor, caseVerifier
        );
        this.walksCalculator = walksCalculator || new NewBaseOnBallsCalculator(
            RankingScopeContext.Pitching.key, this.resultsProcessor, caseVerifier
        );
        this.hitByPitchesCalculator = hitByPitchesCalculator || new NewHitByPitchesCalculator(
            RankingScopeContext.Pitching.key, this.resultsProcessor, caseVerifier
        );
        this.inningsPitchedCalculator = inningsPitchedCalculator || new NewInningsPitchedCalculator(
            this.resultsProcessor, caseVerifier, undefined, this.strikeoutCalculator, this.hitsCalculator, this.walksCalculator
        );
        this.fipConstant = fipConstant;
    }

    /**
     * Calculates given statistical parameter
     * @param {object} parameter - a parameter to calculate, object from Enum PitchingRankingStats, BattingRankingStats or GeneralRankingStats
     * @param {ResultsSlice} slice - result slice with scope
     * @param {object} session - session object, @see https://bitbucket.org/flightscope/dto-schema/src/master/baseball/entity/session-v2.json
     * @param {object} league - league object, @see https://bitbucket.org/flightscope/dto-schema/src/master/baseball/entity/league.json
     * @returns {RankingStat|null} RankingStat instance or null when calculation wasn't possible
     */
    calculateStat(parameter, slice, session, league) {
        // TODO - throw or return nulls ?
        if (!this.strikeoutCalculator.canCalculate(
            PitchingRankingStats.Strikeouts, slice, session, league
        )) {
            throw new Error(`strikeoutCalculator is not able to calculate necessary stats - ${PitchingRankingStats.Strikeouts.key}`);
        }
        if (!this.hitsCalculator.canCalculate(
            PitchingRankingStats.HomeRunsAllowed, slice, session, league
        )) {
            throw new Error(`hitsCalculator is not able to calculate necessary stats - ${PitchingRankingStats.HomeRunsAllowed.key}`);
        }
        if (!this.walksCalculator.canCalculate(
            PitchingRankingStats.WalksAllowed, slice, session, league
        )) {
            throw new Error(`walksCalculator is not able to calculate necessary stats - ${PitchingRankingStats.WalksAllowed.key}`);
        }
        if (!this.hitByPitchesCalculator.canCalculate(
            PitchingRankingStats.HitBatsmen, slice, session, league
        )) {
            throw new Error(`hitByPitchesCalculator is not able to calculate necessary stats - ${PitchingRankingStats.HitBatsmen.key}`);
        }
        if (!this.inningsPitchedCalculator.canCalculate(
            PitchingRankingStats.InningsPitched, slice, session, league
        )) {
            throw new Error(`inningsPitchedCalculator is not able to calculate necessary stats - ${PitchingRankingStats.InningsPitched.key}`);
        }
        if (!this.inningsPitchedCalculator.canCalculate(
            PitchingRankingStats.OutsPitched, slice, session, league
        )) {
            throw new Error(`inningsPitchedCalculator is not able to calculate necessary stats - ${PitchingRankingStats.OutsPitched.key}`);
        }

        const strikeouts = this.strikeoutCalculator.calculate(
            PitchingRankingStats.Strikeouts, slice, session, league
        );
        const walks = this.walksCalculator.calculate(
            PitchingRankingStats.WalksAllowed, slice, session, league
        );
        const hitByPitches = this.hitByPitchesCalculator.calculate(
            PitchingRankingStats.HitBatsmen, slice, session, league
        );
        const homeRuns = this.hitsCalculator.calculate(
            PitchingRankingStats.HomeRunsAllowed, slice, session, league
        );
        const outsPitched = this.inningsPitchedCalculator.calculate(
            PitchingRankingStats.OutsPitched, slice, session, league
        );

        const strikeoutsValue = (!strikeouts || strikeouts.isEmpty()) ? 0 : strikeouts.value;
        const walksValue = (!walks || walks.isEmpty()) ? 0 : walks.value;
        const hitByPitchesValue = (!hitByPitches || hitByPitches.isEmpty()) ? 0 : hitByPitches.value;
        const homeRunsValue = (!homeRuns || homeRuns.isEmpty()) ? 0 : homeRuns.value;
        const outsPitchedValue = (!outsPitched || outsPitched.isEmpty()) ? 0 : outsPitched.value;

        // The Fielding Independent Pitching in baseball is a defense-independent pitching statistic to measure a pitcher's effectiveness
        // based only on statistics that do not involve fielders (except the catcher).
        // These include home runs allowed, strikeouts, hit batters, walks, and, more recently, fly ball percentage, ground ball percentage,
        // and (to much a lesser extent) line drive percentage. By focusing on these statistics, which the pitcher has almost total control over,
        // and ignoring what happens once a ball is put in play, which the pitcher has little control over, DIPS can offer a clearer picture of the pitcher's true ability.
        // @see https://www.mlb.com/glossary/advanced-stats/fielding-independent-pitching
        // @see https://www.baseball-reference.com/bullpen/Fielding_Independent_Pitching
        // @see https://captaincalculator.com/sports/baseball/fielding-independent-pitching-calculator/

        // FIP = ((HR x 13) + (3 x (BB + HBP)) - (2 x K)) / IP + FIP constant.
        const fipDivider = (outsPitchedValue) / 3 + this.fipConstant;
        if (fipDivider === 0 || outsPitchedValue === 0) {
            // TODO - is it even possible ?
            return null;
        }

        const fipDividend   = (13 * homeRunsValue)
                            + (3 * (walksValue + hitByPitchesValue))
                            - (2 * strikeoutsValue);

        const fipParam = this.supportedParams[0];
        return RankingStat.fromParameter(
            fipParam,
            this.numberFormatter.format(fipDividend / fipDivider),
            outsPitchedValue // TODO - this is not perfect, but as general assumption weight must be an integer, consider also Math.round(fipDivider)
        );
    }

    static fipConstantForProfessionals() {
        // @see https://legacy.baseballprospectus.com/glossary/index.php?search=FIP
        return 3.2;
    }

    static fipConstantForAmateurs() {
        // @see https://docs.google.com/document/d/1DjxZU1qm8P7BjTRsQSmNxzEbSBT2Oo0nshhw561PAdc/edit?disco=AAAAH8lM6Uc
        // value recommended by Trevor Cappa
        return 3.75;
    }
}

export default NewFieldingIndependentPitchingCalculator;